/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable complexity */

import type { Payload } from 'echarts/types/src/util/types';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';

// payload重写
export type ActionPayload = Payload & {
  // 用 index 或 name 来指定系列。
  // 可以使用数组指定多个系列。
  seriesIndex?: number | number[];
  seriesName?: string | string[];

  // 用 index 或 name 来指定数据。
  // 可以使用数组指定多个数据。
  dataIndex?: number | number[];
  dataName?: string | string[];

  // 用 dataIndex 或 dataName 来指定数据。
  itemsInfo?: { seriesIndex: number; dataIndex: number }[];
};

// 实际计算的参数格式
export type ActionPayloadApplication = ActionPayload & {
  // 可选，是否开启反选
  reverse?: boolean;
};

/**
 * @param el 目标元素
 * @param stateOperation 状态操作
 * @param stateParams 状态参数
 * @param avoidStates 要避免的状态合集
 * @param statePreprocess 状态预处理函数
 * @returns
 */
export function toggleStateProxy(
  el: any,
  stateOperation: 'add' | 'remove' | 'reset' | 'clear',
  stateParams?: string | string[],
  avoidStates?: string,
  statePreprocess?: (element: any) => void
) {
  if (statePreprocess) {
    statePreprocess(el);
  }
  const stateParamsTemp = Array.isArray(stateParams) ? stateParams : [stateParams];
  if (avoidStates && el.currentStates.includes(avoidStates)) {
    return;
  }
  switch (stateOperation) {
    case 'add':
      if (!stateParamsTemp.length) {
        break;
      }
      el.useStates(Array.from(new Set([...el.currentStates, ...stateParamsTemp])));
      break;
    case 'remove':
      if (!stateParamsTemp.length) {
        break;
      }
      stateParamsTemp.forEach(stateItem => {
        el.removeState(stateItem);
      });
      break;
    case 'reset':
      if (!stateParamsTemp.length) {
        break;
      }
      el.clearStates();
      el.useStates(Array.from(new Set(stateParamsTemp)));
      break;
    case 'clear':
      el.clearStates();
      break;
    default:
      break;
  }
}

// 筛选参数统一
export function getItemsInfo(payload: ActionPayloadApplication, ecModel: GlobalModel) {
  const {
    itemsInfo = [],
    seriesIndex = [],
    seriesName = [],
    dataIndex = [],
    dataName = [],
    reverse = false
  } = payload;
  // 预处理
  const seriesIndexTemp = Array.isArray(seriesIndex) ? seriesIndex : [seriesIndex];
  const seriesNameTemp = Array.isArray(seriesName) ? seriesName : [seriesName];
  const dataIndexTemp = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  const dataNameTemp = Array.isArray(dataName) ? dataName : [dataName];

  const seriesModels = ecModel.getSeries();
  // 传入包含itemsInfo则以该选项为准
  if (!itemsInfo.length) {
    // 未传入itemsInfo的情况下
    // 如果同时未传入index与name则视为全选
    seriesModels.forEach(seriesModelItem => {
      const seriesSelect =
        (seriesIndexTemp.length && seriesIndexTemp.includes(seriesModelItem.seriesIndex)) ||
        (seriesNameTemp.length && seriesNameTemp.includes(seriesModelItem.name)) ||
        (!seriesNameTemp.length && !seriesIndexTemp.length);

      if (!seriesSelect) {
        return;
      }
      const dataCount = seriesModelItem.getData().count();
      const dataIndexArr = Array.from({ length: dataCount }, (v, k) => k);
      dataIndexArr.forEach(dataIdx => {
        const dataParams = seriesModelItem.getDataParams(dataIdx);
        // dataIndex或dataName至少一个有值时，才进行筛选
        const dataSelect =
          (dataIndexTemp.length && dataIndexTemp.includes(dataParams.dataIndex)) ||
          (dataNameTemp.length && dataNameTemp.includes(dataParams.name)) ||
          (!dataNameTemp.length && !dataIndexTemp.length);

        if (!dataSelect) {
          return;
        }

        itemsInfo.push({
          seriesIndex: seriesModelItem.seriesIndex,
          dataIndex: dataParams.dataIndex
        });
      });
    });
  }

  if (reverse) {
    const result = [];
    seriesModels.forEach(seriesModelItem => {
      const { seriesIndex } = seriesModelItem;
      const dataCount = seriesModelItem.getData().count();
      const dataIndexArr = Array.from({ length: dataCount }, (v, k) => k);
      dataIndexArr.forEach(dataIdx => {
        const dataParams = seriesModelItem.getDataParams(dataIdx);
        const { dataIndex } = dataParams;
        const target = itemsInfo.find(d => {
          return d.seriesIndex === seriesIndex && d.dataIndex === dataIndex;
        });
        if (!target) {
          result.push({ seriesIndex, dataIndex });
        }
      });
    });
    return result as typeof itemsInfo;
  }

  return itemsInfo;
}

/**
 * 根据payload中的参数，更新选中元素的状态
 * @param payload acation参数
 * @param ecModel 全局Model
 * @param api ExtensionAPI
 * @param stateOperation 对状态的操作类型
 * @param stateParams 状态操作的参数
 * @param reverse 是否开启搜索范围反选
 */
export function stateHandle(
  payload: ActionPayloadApplication,
  ecModel: GlobalModel,
  api: ExtensionAPI,
  stateOperation: 'add' | 'remove' | 'reset' | 'clear',
  stateParams?: string | string[],
  avoidStates?: string
) {
  const itemsInfo = getItemsInfo(payload, ecModel);
  const seriesModels = ecModel.getSeries();
  seriesModels.forEach(seriesModel => {
    const { seriesIndex } = seriesModel;
    const seriesItemsInfo = itemsInfo.filter(item => item.seriesIndex === seriesIndex);
    if (!seriesItemsInfo.length) {
      return;
    }

    const seriesData = seriesModel.getData();
    const dataIndexs = seriesItemsInfo.map(item => item.dataIndex);
    dataIndexs.forEach(dataIndex => {
      const el = seriesData.getItemGraphicEl(dataIndex);
      if (el) {
        toggleStateProxy(el, stateOperation, stateParams, avoidStates);
        if (el.isGroup) {
          el.traverse(childEl => {
            toggleStateProxy(childEl, stateOperation, stateParams || [], avoidStates);
          });
        }
      }
    });
  });
}
